import type { AvailabilityMode, WorkerView } from '@/types/ui/resource-availability'
import { formatHours, formatPercent, monthLabelFull } from '@/lib/utils/availability-utils'
import { useLanguage } from '@/contexts/language-context'

interface WorkerMonthlyDetailTableProps {
  worker: WorkerView
  mode: AvailabilityMode
}

/**
 * Per-month forecast detail: capacity, allocated hours, stat-holiday + PTO context, and the
 * alloc/avail %. Trailing utilization (billable / non-billable / utilized hours) is
 * intentionally absent — that view lives on the engagement portfolio dashboard's Utilization
 * widget.
 */
export function WorkerMonthlyDetailTable({ worker, mode }: WorkerMonthlyDetailTableProps) {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.monthlyTable
  const pctHeader = mode === 'allocated' ? (t?.allocPct || 'Alloc %') : (t?.availPct || 'Avail %')
  return (
    <div className="rounded-md border bg-card overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead className="bg-muted">
          <tr>
            <th className="text-left px-4 py-2 text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t?.month || 'Month'}</th>
            <th className="px-2 py-2 text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t?.capacity || 'Capacity'}</th>
            <th className="px-2 py-2 text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t?.allocated || 'Allocated'}</th>
            <th className="px-2 py-2 text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t?.stat || 'Stat'}</th>
            <th className="px-2 py-2 text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{t?.pto || 'PTO'}</th>
            <th className="px-2 py-2 text-[10px] uppercase tracking-wide font-medium text-muted-foreground">{pctHeader}</th>
          </tr>
        </thead>
        <tbody>
          {worker.months.map(cell => {
            const pct = mode === 'allocated' ? cell.allocatedPercent : cell.availablePercent
            const rowBg = cell.isCurrentMonth ? 'bg-blue-50/50' : ''
            return (
              <tr key={cell.monthStart} className={`border-t ${rowBg}`}>
                <td className="px-4 py-2 font-medium">{monthLabelFull(cell.monthStart)}</td>
                <td className="px-2 py-2 text-center">{formatHours(cell.capacityHours)}</td>
                <td className="px-2 py-2 text-center text-blue-700 font-medium">{formatHours(cell.plannedHours)}</td>
                <td className="px-2 py-2 text-center text-muted-foreground">{formatHours(cell.statHours)}</td>
                <td className="px-2 py-2 text-center text-muted-foreground">{formatHours(cell.ptoHours)}</td>
                <td className="px-2 py-2 text-center">
                  <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700">
                    {formatPercent(pct)}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
